import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { getOrderDetails, approveRejectCancellation, initiateCancellationRefund } from '../../../api/shop/orders/orderService';
import { OrderDetailData, OrderItemDetail, OrderStatusHistoryItem } from '../../../api/shop/types/orderTypes';
import { OrderStatusBadge } from '../../../Common/BadgeStyles';
import { getImageUrl } from '../../../utils/imageUrl';
import { formatDate } from '../../../utils/formatUtils';
import secureApi from '../../../utils/secureApi';
import { SHOP_ENDPOINTS } from '../../../api/shop/endpoints';
import TrackOrder from './components/TrackOrder';
import OrderItems from './components/OrderItems';
import ContactInformation from './components/ContactInformation';
import RefundMethod from './components/RefundMethod';
import ApproveRejectModal from './components/ApproveRejectModal';
import ApproveReturnModal from './components/ApproveReturnModal';
import InitiateRefundModal from './components/InitiateRefundModal';
import './OrderDetail.css';

const CancelDetail: React.FC = () => {
  const { orderId, orderItemId } = useParams<{ orderId: string; orderItemId: string }>();
  const navigate = useNavigate();
  const [orderDetail, setOrderDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchCancelDetail = async (isRetry = false) => {
    if (!orderId || !orderItemId) {
      setError('Missing order ID or order item ID');
      setLoading(false);
      return;
    }
    
    try {
      if (isRetry) {
        setRetrying(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await getOrderDetails(orderId, orderItemId);
      
      if (!response || !response.data) {
        throw new Error('Invalid response format');
      }
      
      const orderData = response.data as any;
      
      const hasCancellation = 
        orderData.orderItemInfo?.cancellation || 
        orderData.orderItems?.[0]?.cancellation ||
        orderData.orderItems?.[0]?.status === 'CANCELLED' ||
        orderData.orderItemInfo?.status === 'CANCELLED';
      
      const hasCancelledItem = orderData.orderItems?.some((item: any) => 
        item.status === 'CANCELLED' || item.cancellation
      );
      
      if (!hasCancellation && !hasCancelledItem) {
        navigate(`/shop-services/orders/${orderId}/${orderItemId}`, { replace: true });
        return;
      }
      
      setOrderDetail(orderData);
    } catch (err: any) {
      let errorMessage = 'Failed to load cancellation details';
      
      if (err.message?.includes('not found')) {
        errorMessage = 'Order not found or you do not have permission to view it';
      } else if (err.message?.includes('Network Error') || err.message?.includes('ECONNABORTED')) {
        errorMessage = 'Connection timeout. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  useEffect(() => {
    fetchCancelDetail();
  }, [orderId, orderItemId]);

  const getCurrentCancellationStatus = (): string => {
    const orderItemInfo = orderDetail?.orderItemInfo;
    const firstOrderItem = orderDetail?.orderItems?.[0];
    const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
    
    if (!cancellation) return 'REQUESTED';
    
    // Get the latest status from statusHistory
    if (cancellation.statusHistory && cancellation.statusHistory.length > 0) {
      const sortedHistory = [...cancellation.statusHistory].sort((a: any, b: any) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return dateB - dateA;
      });
      return (sortedHistory[0].status || cancellation.status || 'REQUESTED').toUpperCase();
    }
    
    return (cancellation.status || 'REQUESTED').toUpperCase();
  };

  const getTrackingStatus = () => {
    const currentStatus = getCurrentCancellationStatus();
    const orderItemInfo = orderDetail?.orderItemInfo;
    const firstOrderItem = orderDetail?.orderItems?.[0];
    const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
    const refundStatus = cancellation?.refund?.status;
    const hasRefund = !!cancellation?.refund;
    
    if (hasRefund && refundStatus) {
      if (refundStatus === 'PROCESSED' || refundStatus === 'COMPLETED' || refundStatus === 'SUCCESS') {
        return 'REFUNDED';
      }
      if (refundStatus === 'INITIATED') {
        return 'REFUND_INITIATED';
      }
    }
    
    return currentStatus;
  };

  const canApproveReject = () => {
    if (!orderDetail) return false;
    const orderItemInfo = orderDetail?.orderItemInfo;
    const firstOrderItem = orderDetail?.orderItems?.[0];
    const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
    
    if (!cancellation) return false;
    
    // Check statusHistory first - if latest status is REQUESTED, show approve/reject
    if (cancellation.statusHistory && cancellation.statusHistory.length > 0) {
      const sortedHistory = [...cancellation.statusHistory].sort((a: any, b: any) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return dateB - dateA;
      });
      const latestStatus = (sortedHistory[0].status || '').toUpperCase();
      return latestStatus === 'REQUESTED';
    }
    
    // Fallback to cancellation.status
    const status = (cancellation.status || '').toUpperCase();
    return status === 'REQUESTED';
  };

  const canInitiateRefund = () => {
    if (!orderDetail) return false;
    
    const orderItemInfo = orderDetail?.orderItemInfo;
    const firstOrderItem = orderDetail?.orderItems?.[0];
    const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
    
    if (!cancellation) return false;
    
    // Get current status - check both statusHistory and cancellation.status
    let currentStatus = 'REQUESTED';
    
    // First check statusHistory (most reliable)
    if (cancellation.statusHistory && cancellation.statusHistory.length > 0) {
      const sortedHistory = [...cancellation.statusHistory].sort((a: any, b: any) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return dateB - dateA;
      });
      const latestStatusHistory = sortedHistory[0]?.status;
      if (latestStatusHistory) {
        currentStatus = latestStatusHistory.toUpperCase();
      }
    }
    
    // Fallback to cancellation.status if statusHistory doesn't have APPROVED
    if (currentStatus !== 'APPROVED' && cancellation.status) {
      currentStatus = cancellation.status.toUpperCase();
    }
    
    // Only allow refund initiation if status is APPROVED (not REJECTED)
    if (currentStatus !== 'APPROVED') {
      return false;
    }
    
    // Check if refund already exists and has been initiated
    // A refund object with empty statusHistory means no refund has been initiated yet
    const refund = cancellation?.refund;
    if (!refund) {
      return true; // No refund object, can initiate
    }
    
    // Check if refund has been initiated (has status or statusHistory with entries)
    const refundStatus = refund?.status;
    const refundStatusHistory = refund?.statusHistory;
    
    // If refund has a status, it means refund has been initiated
    if (refundStatus) {
      return false; // Refund already initiated
    }
    
    // If refund has statusHistory with entries, refund has been initiated
    if (refundStatusHistory && Array.isArray(refundStatusHistory) && refundStatusHistory.length > 0) {
      return false; // Refund already initiated
    }
    
    // Refund object exists but has no status or statusHistory entries, so refund hasn't been initiated yet
    return true;
  };

  const getRefundStatus = () => {
    const orderItemInfo = orderDetail?.orderItemInfo;
    const firstOrderItem = orderDetail?.orderItems?.[0];
    const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
    const refund = cancellation?.refund;
    if (refund?.status) {
      return refund.status.toUpperCase();
    }
    return null;
  };

  const getTrackingSteps = () => {
    if (!orderDetail) return [];

    const orderItemInfo = orderDetail?.orderItemInfo;
    const firstOrderItem = orderDetail?.orderItems?.[0];
    
    const orderStatusHistory = orderItemInfo?.orderStatusHistory || firstOrderItem?.orderStatusHistory || [];
    const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
    
    if (!cancellation) return [];

    const steps = [];

    // Step 1: Order Placed (CONFIRMED)
    const orderPlacedStep = orderStatusHistory.find((step: any) => step.status === 'CONFIRMED');
    steps.push({
      status: 'CONFIRMED',
      title: 'Order Placed',
      number: '01',
      date: orderPlacedStep?.statusDateLabel,
      updatedAt: orderPlacedStep?.updatedAt
    });

    // Step 2: Cancel Requested - Use cancellation.statusHistory
    const cancelRequestedStep = cancellation.statusHistory?.find((step: any) => step.status === 'REQUESTED');
    steps.push({
      status: 'REQUESTED',
      title: 'Cancel Requested',
      number: '02',
      date: cancelRequestedStep?.updatedAt ? formatDate(cancelRequestedStep.updatedAt) : formatDate(cancellation.cancelledAt),
      updatedAt: cancelRequestedStep?.updatedAt || cancellation.cancelledAt
    });

    // Step 3: Cancel Approved/Rejected - Always show this step
    // Check if decision has been made (APPROVED or REJECTED)
    const approvedStep = cancellation.statusHistory?.find((step: any) => step.status === 'APPROVED');
    const rejectedStep = cancellation.statusHistory?.find((step: any) => step.status === 'REJECTED');
    const decisionStep = approvedStep || rejectedStep;
    
    // Always add the decision step, even if not decided yet
    if (decisionStep) {
      // Decision has been made
      steps.push({
        status: decisionStep.status === 'APPROVED' ? 'APPROVED' : 'REJECTED',
        title: decisionStep.status === 'APPROVED' ? 'Cancel Approved' : 'Cancel Rejected',
        number: '03',
        date: decisionStep.updatedAt ? formatDate(decisionStep.updatedAt) : undefined,
        updatedAt: decisionStep.updatedAt
      });
    } else {
      // Decision hasn't been made yet - show as pending step
      // We'll use APPROVED as the status since it's the expected next step
      // The TrackOrder component will handle showing it as pending
      steps.push({
        status: 'APPROVED', // Use APPROVED as placeholder, TrackOrder will show as pending
        title: 'Cancel Approved',
        number: '03',
        date: undefined,
        updatedAt: undefined
      });
    }

    // Step 4: Refund Initiated - Always show this step if cancellation is not rejected
    const currentStatus = getCurrentCancellationStatus();
    const refund = cancellation?.refund;
    
    // Only show refund step if cancellation is not rejected
    if (currentStatus !== 'REJECTED') {
      if (refund) {
        // Refund exists - show with actual data
        const refundInitiatedStep = refund.statusHistory?.find((step: any) => step.status === 'INITIATED');
        steps.push({
          status: refund.status === 'PROCESSED' ? 'REFUND_PROCESSED' : 'REFUND_INITIATED',
          title: 'Refund Initiated',
          number: '04',
          date: refundInitiatedStep?.date ? formatDate(refundInitiatedStep.date) : (refund.statusUpdatedAt ? formatDate(refund.statusUpdatedAt) : undefined),
          updatedAt: refundInitiatedStep?.date || refund.statusUpdatedAt
        });
      } else {
        // Refund doesn't exist yet - show as pending step
        steps.push({
          status: 'REFUND_INITIATED', // Placeholder status, TrackOrder will show as pending
          title: 'Refund Initiated',
          number: '04',
          date: undefined,
          updatedAt: undefined
        });
      }
    }

    return steps;
  };

  const handleApprove = () => {
    setShowApproveModal(true);
  };

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleConfirmApprove = async () => {
    if (!orderId || !orderItemId) return;
    
    try {
      setIsProcessing(true);
      await approveRejectCancellation(orderId, orderItemId, 'APPROVED', '');
      
      setShowApproveModal(false);
      await fetchCancelDetail(true); // Refresh cancellation data
    } catch (error) {
      alert('Failed to approve cancellation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmReject = async (reason: string) => {
    if (!orderId || !orderItemId) return;
    
    try {
      setIsProcessing(true);
      await approveRejectCancellation(orderId, orderItemId, 'REJECTED', reason);
      
      setShowRejectModal(false);
      await fetchCancelDetail(true); // Refresh cancellation data
    } catch (error) {
      alert('Failed to reject cancellation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInitiateRefund = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Prevent opening if already open or processing
    if (showRefundModal || isProcessing) {
      return;
    }
    setShowRefundModal(true);
  };

  const handleConfirmRefund = async () => {
    if (!orderId || !orderItemId) return;
    
    try {
      setIsProcessing(true);
      await initiateCancellationRefund(orderId, orderItemId);
      
      setShowRefundModal(false);
      await fetchCancelDetail(true); // Refresh cancellation data
    } catch (error) {
      alert('Failed to initiate refund. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getCancellationDetails = () => {
    const orderItemInfo = orderDetail?.orderItemInfo;
    const firstOrderItem = orderDetail?.orderItems?.[0];
    const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
    
    if (!cancellation) return null;

    // Get rejection reason from statusHistory if status is REJECTED
    let rejectionReason = null;
    const currentStatus = getCurrentCancellationStatus();
    if (currentStatus === 'REJECTED' && cancellation.statusHistory) {
      const rejectedStep = cancellation.statusHistory.find((step: any) => step.status === 'REJECTED');
      if (rejectedStep && rejectedStep.reason) {
        rejectionReason = rejectedStep.reason;
      }
    }

    // Format cancelledByRole (capitalize first letter)
    const cancelledByRole = cancellation.cancelledByRole 
      ? cancellation.cancelledByRole.charAt(0).toUpperCase() + cancellation.cancelledByRole.slice(1).toLowerCase()
      : null;

    return {
      cancelledAt: formatDate(cancellation.cancelledAt),
      reason: cancellation.reason,
      cancelledByRole: cancelledByRole, // Add cancelled by role
      rejectionReason: rejectionReason, // Add rejection reason
      additionalDetails: cancellation.additionalDetails,
      refundAmount: cancellation.refund?.amount || cancellation.refundPaymentDetails?.amount || 0,
      refundStatus: cancellation.refund?.status,
      paymentMethod: cancellation.refundPaymentDetails?.paymentMethod || orderDetail?.paymentMethod?.method || '',
      upiId: cancellation.refundPaymentDetails?.upiId,
      status: currentStatus // Include current status
    };
  };

  const getCurrentOrderItem = () => {
    if (orderDetail?.orderItemInfo) {
      const itemInfo = orderDetail.orderItemInfo;
      return {
        _id: itemInfo._id,
        productName: itemInfo.productName || '',
        image: itemInfo.image || '',
        price: itemInfo.price || 0,
        quantity: itemInfo.quantity || 1,
        color: itemInfo.color || '',
        size: itemInfo.size || '',
        seller: itemInfo.seller || '',
        status: itemInfo.status || 'CANCELLED',
        orderStatusHistory: itemInfo.orderStatusHistory || [],
        sellerLocation: orderDetail.orderItemInfo.sellerLocation || { name: '', address: '' }
      };
    }
    
    if (orderDetail?.orderItems && orderDetail.orderItems.length > 0) {
      return orderDetail.orderItems[0];
    }
    
    return null;
  };

  const handleRetry = () => {
    fetchCancelDetail(true);
  };

  if (loading && !retrying) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
              { label: 'Orders', onClick: () => navigate('/shop-services/orders') },
              { label: 'Cancelled Details', active: true }
            ]} />
          </div>
          <div style={{ padding: '0 15px' }}>
            <ShimmerLoader type="table" rows={8} />
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
              { label: 'Orders', onClick: () => navigate('/shop-services/orders') },
              { label: 'Cancelled Details', active: true }
            ]} />
          </div>
          <div style={{ padding: '0 15px' }}>
            <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
              <div>
                <i className="ri-error-warning-line me-2"></i>
                {error}
              </div>
              <button 
                className="btn btn-outline-danger btn-sm"
                onClick={handleRetry}
                disabled={retrying}
              >
                {retrying ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Retrying...
                  </>
                ) : (
                  <>
                    <i className="ri-refresh-line me-1"></i>
                    Retry
                  </>
                )}
              </button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!orderDetail) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
              { label: 'Orders', onClick: () => navigate('/shop-services/orders') },
              { label: 'Cancelled Details', active: true }
            ]} />
          </div>
          <div style={{ padding: '0 15px' }}>
            <div className="alert alert-warning" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              No cancellation details found for this order.
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const currentOrderItem = getCurrentOrderItem();
  if (!currentOrderItem) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
              { label: 'Orders', onClick: () => navigate('/shop-services/orders') },
              { label: 'Cancelled Details', active: true }
            ]} />
          </div>
          <div style={{ padding: '0 15px' }}>
            <div className="alert alert-warning" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              Unable to load order item details.
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const cancellationDetails = getCancellationDetails();
  const orderItemInfo = orderDetail.orderItemInfo;
  const firstOrderItem = orderDetail.orderItems?.[0];
  
  const orderStatusHistory = orderItemInfo?.orderStatusHistory || firstOrderItem?.orderStatusHistory || [];
  const cancellation = orderItemInfo?.cancellation || firstOrderItem?.cancellation;
  const refundStatus = cancellation?.refund?.status;
  
  const rawPaymentMethod = cancellation?.refundPaymentDetails?.paymentMethod || orderDetail?.paymentMethod?.method || '';
  const normalizedPaymentMethod = rawPaymentMethod.toLowerCase().replace(/_/g, '');
  
  const paymentMethodData = orderDetail?.paymentMethod;
  const actualCardLastFour = typeof paymentMethodData === 'object' && paymentMethodData !== null 
    ? (paymentMethodData.cardLastFour || '') 
    : '';
  const actualCardType = typeof paymentMethodData === 'object' && paymentMethodData !== null 
    ? (paymentMethodData.cardType || '') 
    : '';
  
  const trackingSteps = getTrackingSteps();

  return (
    <div className="page-content order-detail-page" style={{ marginTop: '20px', paddingBottom: '100px' }}>
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
            { label: 'Orders', onClick: () => navigate('/shop-services/orders') },
            { label: 'Cancelled Details', active: true }
          ]} />
        </div>

        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <button 
              className="back-button"
              onClick={() => navigate(-1)}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="page-title mb-0">Cancelled Details</h1>
          </div>
        </div>

        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <div className="order-summary-card">
            <div className="order-summary-content">
              <div className="order-summary-header">
                <div className="order-summary-left">
                  <div className="order-summary-icon">
                    <img 
                      src={getImageUrl('admin/static-assets/icons/shop-service/Container.svg')} 
                      alt="Order Container" 
                      className="container-icon"
                    />
                  </div>
                  <div className="order-summary-info">
                    <div className="order-summary-title-row">
                      <h4 className="order-summary-title">{currentOrderItem.productName}</h4>
                      <OrderStatusBadge status="CANCELLED" />
                    </div>
                    <p className="order-summary-date">{formatDate(orderDetail.orderDate)}</p>
                  </div>
                </div>
                <div className="order-summary-right">
                  <div className="order-summary-id">
                    Order ID: <span>{orderDetail.id}</span>
                  </div>
                  <button 
                    className="header-invoice-btn"
                    onClick={async () => {
                      try {
                        const isMongoIdFormat = (id: string) => id && /^[0-9a-fA-F]{24}$/.test(id);
                        
                        if (!orderDetail) {
                          alert('Order details not loaded. Please refresh the page.');
                          return;
                        }
                        
                        const apiResponseOrderId = orderDetail.orderId;
                        const apiResponseOrderItemId = orderDetail.orderItemInfo?._id;
                        const orderNumberString = orderDetail.id;
                        
                        let mongoOrderId: string = '';
                        if (apiResponseOrderId && 
                            isMongoIdFormat(apiResponseOrderId) && 
                            apiResponseOrderId !== orderNumberString) {
                          mongoOrderId = String(apiResponseOrderId).trim();
                        } else if (orderId && isMongoIdFormat(orderId)) {
                          mongoOrderId = String(orderId).trim();
                        } else {
                          alert(`Invalid order ID. Expected MongoDB ID but got: ${apiResponseOrderId || 'undefined'}. Please refresh the page.`);
                          return;
                        }
                        
                        let mongoOrderItemId: string = '';
                        if (apiResponseOrderItemId && isMongoIdFormat(apiResponseOrderItemId)) {
                          mongoOrderItemId = String(apiResponseOrderItemId).trim();
                        } else if (orderItemId && isMongoIdFormat(orderItemId)) {
                          mongoOrderItemId = String(orderItemId).trim();
                        } else {
                          alert(`Invalid order item ID. Expected MongoDB ID but got: ${apiResponseOrderItemId || 'undefined'}. Please refresh the page.`);
                          return;
                        }
                        
                        if (mongoOrderId === orderNumberString || !isMongoIdFormat(mongoOrderId)) {
                          alert('Error: Invalid order ID detected. Please refresh the page.');
                          return;
                        }
                        
                        if (!isMongoIdFormat(mongoOrderItemId)) {
                          alert('Error: Invalid order item ID detected. Please refresh the page.');
                          return;
                        }
                        
                        const invoiceEndpoint = SHOP_ENDPOINTS.ORDERS.GET_INVOICE(mongoOrderId, mongoOrderItemId);
                        
                        if (invoiceEndpoint.includes(orderDetail.id) || invoiceEndpoint.includes('SFLOR')) {
                          alert('Error: Invalid order ID detected in endpoint. Please refresh the page.');
                          return;
                        }
                        
                        if (!invoiceEndpoint.includes(mongoOrderId) || !invoiceEndpoint.includes(mongoOrderItemId)) {
                          alert('Error: Invalid endpoint construction. Please refresh the page.');
                          return;
                        }
                        const response = await secureApi.get(invoiceEndpoint);
                        const invoiceUrl = response?.invoiceUrl || response?.data?.invoiceUrl || response?.url || response?.data?.url || response?.link || response?.data?.link;
                        if (invoiceUrl) {
                          window.open(invoiceUrl, '_blank');
                        } else {
                          const blob = new Blob([response], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `invoice-${mongoOrderId}-${mongoOrderItemId}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        }
                      } catch (error) {
                        alert('Error downloading invoice. Please try again.');
                      }
                    }}
                  >
                    Get Invoice
                  </button>
                </div>
              </div>

              <div className="order-track-section">
                <TrackOrder 
                  orderStatusHistory={orderStatusHistory}
                  currentStatus={getTrackingStatus()}
                  title="Track Cancellation"
                  refundStatus={getRefundStatus()}
                  trackingSteps={trackingSteps}
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <Row className="align-items-stretch">
            <Col lg={6} className="d-flex">
              <OrderItems 
                orderItems={[currentOrderItem]}
                paymentMethod={normalizedPaymentMethod}
                sellerName={currentOrderItem.seller}
                orderId={orderDetail.orderId}
                orderItemId={currentOrderItem._id || orderItemId || ''}
                showViewPaymentInfo={true}
              />
            </Col>
            
            <Col lg={6} className="d-flex">
              <div className="order-detail-right-column">
                <ContactInformation contact={orderDetail.contact} />
                
                {cancellationDetails && (
                  <div className="contact-information-container">
                    <h3 className="contact-information-title">Cancellation Details</h3>
                    
                    <div className="contact-info-grid">
                      <div className="contact-info-row">
                        {cancellationDetails.cancelledAt && (
                          <div className="contact-info-item">
                            <span className="contact-items-label">Cancelled on</span>
                            <span className="contact-items-value">
                              {cancellationDetails.cancelledAt}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Cancellation Reason with Role */}
                      {cancellationDetails.reason && (
                        <div className="contact-info-row">
                          <div className="contact-info-item full-width">
                            <span className="contact-items-label">Cancelled by</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {cancellationDetails.cancelledByRole && (
                                <span className="contact-items-value" style={{ fontWeight: 500, color: '#495057', marginBottom: '4px' }}>
                                  {cancellationDetails.cancelledByRole}
                                </span>
                              )}
                              <span className="contact-items-value">
                                {cancellationDetails.reason}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {cancellationDetails.rejectionReason && (
                        <div className="contact-info-row">
                          <div className="contact-info-item full-width">
                            <span className="contact-items-label">Rejection Reason</span>
                            <span className="contact-items-value" style={{ color: '#dc3545' }}>
                              {cancellationDetails.rejectionReason}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {cancellationDetails.additionalDetails && (
                        <div className="contact-info-row">
                          <div className="contact-info-item full-width">
                            <span className="contact-items-label">Additional details</span>
                            <span className="contact-items-value">
                              {cancellationDetails.additionalDetails}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* Refund Method Section */}
        {cancellationDetails && cancellationDetails.refundAmount > 0 && (
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <Row className="justify-content-start">
              <Col lg={6} xs={12} style={{ paddingLeft: '0', paddingRight: '12px' }}>
                <RefundMethod
                  totalRefundAmount={cancellationDetails.refundAmount}
                  paymentMethod={normalizedPaymentMethod}
                  cardLastFour={actualCardLastFour}
                  cardType={actualCardType}
                />
              </Col>
            </Row>
          </div>
        )}

        {/* Sticky Action Bar - Match ReturnDetail design */}
        {(() => {
          // Calculate button visibility
          const showApproveReject = canApproveReject();
          const showInitiateRefund = canInitiateRefund();
          const showButtons = showApproveReject || showInitiateRefund;
          
          if (!showButtons) return null;
          
          return (
            <div className="order-detail-actions">
              <Container fluid>
                <div className="order-detail-action-bar-content">
                  {showApproveReject ? (
                    <>
                      <Button
                        variant="outline-danger"
                        className="order-detail-action-cancel"
                        onClick={handleReject}
                        disabled={isProcessing}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="primary"
                        className="order-detail-action-confirm"
                        onClick={handleApprove}
                        disabled={isProcessing}
                      >
                        Approve
                      </Button>
                    </>
                  ) : showInitiateRefund ? (
                    <>
                      <Button
                        variant="outline-secondary"
                        className="order-detail-action-cancel"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        className="order-detail-action-confirm"
                        onClick={handleInitiateRefund}
                        disabled={isProcessing || showRefundModal}
                      >
                        Initiate Refund
                      </Button>
                    </>
                  ) : null}
                </div>
              </Container>
            </div>
          );
        })()}

        {/* Modals */}
        <ApproveReturnModal
          show={showApproveModal}
          handleClose={() => setShowApproveModal(false)}
          handleConfirm={handleConfirmApprove}
          isLoading={isProcessing}
          context="cancellation"
        />
        
        <ApproveRejectModal
          show={showRejectModal}
          handleClose={() => setShowRejectModal(false)}
          handleConfirm={handleConfirmReject}
          actionType="reject"
          isLoading={isProcessing}
          context="cancellation"
        />
        
        <InitiateRefundModal
          show={showRefundModal}
          handleClose={() => setShowRefundModal(false)}
          handleConfirm={handleConfirmRefund}
          refundAmount={(() => {
            if (cancellationDetails?.refundAmount && cancellationDetails.refundAmount > 0) {
              return cancellationDetails.refundAmount;
            }
            const orderItemInfo = orderDetail?.orderItemInfo;
            const firstOrderItem = orderDetail?.orderItems?.[0];
            const totalAmount = orderItemInfo?.priceBreakdown?.totalAmount || 
                               firstOrderItem?.priceBreakdown?.totalAmount ||
                               orderItemInfo?.feeBreakup?.totalAmount ||
                               orderDetail?.feeBreakup?.totalAmount;
            
            if (totalAmount && totalAmount > 0) {
              return totalAmount;
            }
            
            return currentOrderItem?.price || 0;
          })()}
          isLoading={isProcessing}
        />
      </Container>
    </div>
  );
};

export default CancelDetail;
